/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Row } from 'react-bootstrap';
import { ColumnWidth, DealTransactionType, Roles, TableName } from 'modules/core/enums';
import {
  DEFAULT_PLACEHOLDER_TITLE,
  DEFAULT_PLACEHOLDER_TITLE_SHOP_BY_CALLBACK,
} from 'modules/ProbabilityEstimator/probabilityEstimator.const';
import { VehicleSearchTable } from 'modules/VehicleSearchTable';
import { useTablePlaceholderType } from 'modules/VehicleSearchTable/hooks';
import { ApplyCpoCheckbox, Loader } from 'modules/core/components';
import { FormProvider } from 'react-hook-form';
import {
  lenderSlice,
  resetByStock,
  resetVehicles,
  resetVehiclesByParams,
  resetVehiclesByStock,
  setIsTableView,
  setDealSourceTierEditable,
  setFirstDealSourceRenderOnLender,
  resetResetCount,
} from 'store/reducers/lenders.reducer';
import {
  useDebounceCallback,
  useDebounceVariable,
  useFeesAndTaxesInitialValues,
  useFormEnhanced,
  useModalState,
  useSettingsInitialValues,
  useHiddenScrollBar,
} from 'modules/core/hooks';
import { Trans, useTranslation } from 'react-i18next';
import {
  getCurrentLenderId,
  getFetchingLenderFlag,
  getCurrentLenderControls,
  getIsTableView,
  getLenderInitialErrors,
  getLenderInitialValues,
  getLenderReportData,
  getAvailableFilters,
  getLenderState,
  getLenderVehiclesByParams,
  getLenderVehiclesByStock,
  getLoadedLenderFlag,
  getNotAvailableVehicles,
  getNotFoundVehicles,
  getVehicleFetchingFlags,
  getTiers,
  getLendersList,
} from 'store/selectors/lenders.selector';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { EMPTY_STRING, IS_VALID_DELAY, MODAL_DIALOG_ID, REQUEST_DELAY } from 'modules/core/constants';
import { ConsumerInformation } from 'modules/ProbabilityEstimator/ConsumerInformation';
import { AppDispatch } from 'store/store';
import {
  getSubmitVehicleObjectQuery,
  getTotalVehicles,
  hasCpovDecisionComment,
  isAllyCpoConfiguredForLender,
  isCertifiedUsedCondition,
  userHasInventory,
} from 'modules/core/utils';
import {
  LENDER_BY_STOCK_FORM_NAMES_MAPPER,
  LENDER_FORM_NAMES_MAPPER,
} from 'modules/core/constants/mappers/submitVehicles.mapper';
import { getLenderVehiclesByStockThunk, getVehiclesForLenderThunk } from 'actions/vehicleActions';
import { useSearchTracking } from 'modules/core/hooks/useSearchTracking';
import { PageName } from 'modules/ProbabilityEstimator/ConsumerInformation/hooks/useConsumerInformationData';
import { getLenderControlsThunk, getLenderThunk } from 'actions/lenderAction';
import vehicleApi from 'api/vehicleApi';
import { LenderVehicleCard } from 'modules/core/components/Table/VehicleCardView/LenderVehicleCard/LenderVehicleCard';
import { AdaptedCol } from 'modules/ProbabilityEstimator/styles';
import { DisabledContainer } from 'common/styles';
import { useSwitchFilters } from 'modules/core/hooks/useSwitchFilters';
import { SetupTaxRateModal } from 'components/Modals/SetupTaxRateModal/SetupTaxRateModal';
import {
  getAddUserVehicleFlags,
  getUpdateUserVehicleFlags,
  getCreatedUserVehicle,
  getCreatedUserVehicleTableName,
  getCreatedUserVehicleLenderId,
  getIsDealSourceVehicle,
} from 'store/selectors/vehicle.selector';
import { clearLoanVariablesCurrentVehicle } from 'store/reducers/RACAffordabilityEngine.reducer';
import {
  setAddedIsUserVehicle,
  resetAddedIsUserVehicle,
  setIsDealSourceVehicle,
  resetIsDealSourceVehicle,
  resetCreatedUserVehicle,
} from 'store/reducers/vehicle.reducer';
import { getLoanVariablesCurrentVehicle } from 'store/selectors/RACAffordabilityEngine.selector';
import { setIsModalsDisabled } from 'store/reducers/ui.reducer';
import { FEES_AND_TAXES_FIELD_NAMES, FEES_AND_TAXES_INITIAL_VALUES } from 'modules/core/shared';
import { ADVANCED_OVERRIDES_FIELDS } from 'modules/core/shared/preferencesConfig.const';
import {
  IS_SSO_AND_SHOW_LOAN_VARIABLES_DIALOG,
  DEALER_SSO_LENDER_SELECTED_EVENT,
} from 'modules/auth/constants/auth.const';
import { getUser } from 'store/selectors/auth.selector';
import { getIsModalsDisabled, getDisabledFeaturesList } from 'store/selectors/ui.selector';
import { useDebounceForLoading } from 'router/utils/useDebounceForLoading';
import { TitleContainer } from 'modules/core/components/PageHeader/TitleContainer/TitleContainer';
import { useRequestBalloonInitData } from 'components/Modals/LoanVariablesDialog/balloonPayment/hooks/useRequestBalloonInitData';
import { getRouteOneDealersThunk } from 'actions/dealerActions';
import { useFormOverrideApr } from 'hooks/useFormOverrideApr';
import { useFormOverrideLtv } from 'hooks/useFormOverrideLtv';
import { useDealSourceData } from 'hooks/useDealSourceData';

import { ILenderForm } from './types/Lender.types';
import { REHASH_CALCULATOR_FIELDS, TIER_NAME } from './RehashCalculator/RehashCalculator.const';
import { SHOP_BY_CALLBACK_FIELDS } from './RehashCalculator/ShopByCallbackFieldConfig.const';
import { LenderService } from './services/Lender.service';
import { FilterView } from '../core/enums/filterView.enum';
import { ILenderVehicle } from '../core/types';
import { LTV_INC_TAXES_FEES } from './Lender.const';
import { useDealSourceValues } from '../RACAffordabilityEngine/hooks/useDealSourceValues';
import { FRONT_AND_BACK_ENDS_ADDS_FIELD_NAMES } from '../RACAffordabilityEngine/const/fieldNames.const';
import { useArraFinanceBackAdds } from '../core/hooks/useArraFinanceBackAdds';
import { useHideLenderVehicleField } from '../core/hooks/useHideVehicleField';
import { DisabledModals } from '../core/enums/disabledModals';
import { LenderLoanVariablesDialog } from './LenderLoanVariablesDialog';
import { useLenderMode } from './hooks/useLenderMode';
import { isDealerSsoLenderSelectionComplete } from '../auth/utils/dealerSsoLenderSelection.utils';

export const Lender: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const addVehicleFlags = useSelector(getAddUserVehicleFlags);
  const updateVehicleFlags = useSelector(getUpdateUserVehicleFlags);
  const createdVehicle = useSelector(getCreatedUserVehicle);
  const currentVehicleData = useSelector(getLoanVariablesCurrentVehicle);
  const lenderState = useSelector(getLenderState);
  const { dealSourceData, dealSourceDataFlags, firstDealSourceRender, isDealSourceDataEmpty } = useDealSourceData(
    TableName.LENDER,
  );
  const user = useSelector(getUser);
  const lenders = useSelector(getLendersList);
  const disabledFeaturesList = useSelector(getDisabledFeaturesList);
  const isSalesperson = user?.role === Roles.SALESPERSON;
  const tiers = useSelector(getTiers);
  const isLoadedLender = useSelector(getLoadedLenderFlag);
  const isModalsDisabled = useSelector(getIsModalsDisabled);
  const vehiclesByParams = useSelector(getLenderVehiclesByParams);
  const vehiclesByStock = useSelector(getLenderVehiclesByStock);
  const availableFilters = useSelector(getAvailableFilters);
  const notAvailableVehicles = useSelector(getNotAvailableVehicles);
  const notFoundVehicles = useSelector(getNotFoundVehicles);
  const isTableView = useSelector(getIsTableView);
  const { name: lenderName, id: lenderId } = lenderState;
  const isFetchingLender = useSelector(getFetchingLenderFlag);
  const isLenderLoading = useDebounceForLoading(isFetchingLender);
  const createdUserVehicleTableName = useSelector(getCreatedUserVehicleTableName);
  const createdUserVehicleLenderId = useSelector(getCreatedUserVehicleLenderId);
  const currentLenderId = useSelector(getCurrentLenderId);
  const { showAdvancedOverrides } = useSelector(getCurrentLenderControls);
  const { resetCount, isFetchingVehicles, isFetchingVehiclesFailed } = useSelector(
    getVehicleFetchingFlags,
    shallowEqual,
  );
  const { trackSearch } = useSearchTracking({
    resetCount,
    conversationId: dealSourceData?.conversation_id,
    lender: lenderState?.route_one_id ? { type: 'route_one_id', id: lenderState.route_one_id } : null,
  });

  const {
    handleCloseDialog: handleLoanCloseDialog,
    handleOpenDialog: handleLoanOpenDialog,
    isModalDialogActive: isModalLoanDialogActive,
  } = useModalState([MODAL_DIALOG_ID.LOAN_VARIABLES]);
  const isDealSourceVehicle = useSelector(getIsDealSourceVehicle);
  const isSsoAndLoanVariablesDialogOpen = sessionStorage.getItem(IS_SSO_AND_SHOW_LOAN_VARIABLES_DIALOG);
  const [isActive, setIsActive] = useState(true);
  const [showLoader, setShowLoader] = useState(false);
  const [addedVehicleId, setAddedVehicleId] = useState<number>(0);
  const [showCreatedVehicleErrorMessage, setShowCreatedVehicleErrorMessage] = useState(false);
  const [showDealSourceLenderControlsMismatch, setShowDealSourceLenderControlsMismatch] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [dealerSsoLenderSelectionTick, setDealerSsoLenderSelectionTick] = useState(0);
  const dealSourceLoanTermsRequestKeyRef = useRef<string | null>(null);
  const isApplyCpoToggledByUserRef = useRef(false);

  useEffect(() => {
    const handleDealerSsoLenderSelected = () => setDealerSsoLenderSelectionTick((tick) => tick + 1);

    window.addEventListener(DEALER_SSO_LENDER_SELECTED_EVENT, handleDealerSsoLenderSelected);

    return () => window.removeEventListener(DEALER_SSO_LENDER_SELECTED_EVENT, handleDealerSsoLenderSelected);
  }, []);
  const {
    isWithoutSSO,
    lenderHasRouteOneConfig,
    hasValidDealSourceIncome,
    isShopByBudgetMode,
    isShopByCallbackMode,
    isRouteOneFlowForSbbOrSbc,
    isDealerSsoSbbOrSbcFlow,
    isMakeItWork,
    isSubmitR1DealBlockedByControls,
  } = useLenderMode(user, lenderState, dealSourceData);

  const isRouteOneFlow = isMakeItWork && !isDealSourceDataEmpty;

  const { methods, syncFormAction } = useFormEnhanced<ILenderForm>(
    lenderSlice.actions.syncInitialValues,
    lenderSlice.actions.syncInitialErrors,
    getLenderInitialValues,
    getLenderInitialErrors,
    {
      mode: 'onChange',
      shouldUnregister: false,
    },
    isLenderLoading,
  );

  const {
    setValue,
    watch,
    getValues,
    formState: { isValid, errors },
  } = methods;

  const { getOverrideApr } = useFormOverrideApr(getValues);
  const { getLtvHighLimit } = useFormOverrideLtv(getValues);

  const isFormValid = () => {
    const trackableForms = Object.keys(errors).filter((key) => key !== MODAL_DIALOG_ID.PREFERENCES);

    return trackableForms.length === 0 && !isValid ? true : isValid;
  };

  const {
    tier,
    vehicleFilters,
    cashDownPayment,
    desiredMonthlyPayment,
    grossMonthlyIncome,
    tableView,
    stocks,
    filterView,
    preferences,
    feesAndTaxes,
    routeOneSearch,
    routeOneDealer,
    routeOneDealerId,
    shopByCallback,
    totalFrontAndBackEndsAddsDialog,
    totalNetDownPaymentDialog,
    applyCpo,
  } = watch();

  const r1DealerId =
    (user?.isWithInventory ? routeOneDealer?.value : routeOneDealerId) || user?.originalExternalDealerId || undefined;
  const conversationId = dealSourceData?.conversation_id;

  const isAllyCpoLender = isAllyCpoConfiguredForLender(lenderState?.guidebooks);
  const dealSourceCondition = dealSourceData?.sale_vehicle?.condition;
  const dealSourceDecisionComments = dealSourceData?.decision_comments;

  useEffect(() => {
    if (!isAllyCpoLender) return;

    const seedKey = `${lenderId}|${conversationId ?? ''}`;

    if (getValues('applyCpoSeedKey') === seedKey) return;

    setValue('applyCpoSeedKey', seedKey);
    setValue(
      'applyCpo',
      isCertifiedUsedCondition(dealSourceCondition) || hasCpovDecisionComment(dealSourceDecisionComments),
    );
  }, [isAllyCpoLender, lenderId, conversationId, dealSourceCondition, dealSourceDecisionComments, setValue, getValues]);

  const commonDependencies = [
    tier.tier_name,
    grossMonthlyIncome,
    desiredMonthlyPayment,
    ...(isShopByCallbackMode && shopByCallback
      ? [shopByCallback.sbcTerm?.value, shopByCallback.sbcApr, shopByCallback.sbcLtv]
      : []),
    ...Object.values(preferences),
    ...Object.values(feesAndTaxes),
    totalFrontAndBackEndsAddsDialog?.addOns,
    totalFrontAndBackEndsAddsDialog?.vsc,
    totalFrontAndBackEndsAddsDialog?.gap,
    totalNetDownPaymentDialog?.cashDown,
    totalNetDownPaymentDialog?.tradeValue,
    totalNetDownPaymentDialog?.amountOwed,
    totalNetDownPaymentDialog?.totalNetDownPayment,
    applyCpo,
  ];

  const { currentFilterName, isValidStock, isRequestNeeded } = useSwitchFilters(
    commonDependencies,
    filterView,
    isFetchingVehiclesFailed,
    watch,
    setValue,
  );

  useHiddenScrollBar(isModalLoanDialogActive);

  useEffect(() => {
    if (isRequestNeeded) {
      if (filterView === FilterView.BY_PARAMS) dispatch(resetVehiclesByParams());
      else dispatch(resetVehiclesByStock());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filterView]);

  useEffect(() => {
    if (!isFetchingVehicles) setIsFetching(false);
  }, [isFetchingVehicles]);

  const vehiclesData: ILenderVehicle[] | undefined =
    filterView === FilterView.BY_PARAMS ? vehiclesByParams : vehiclesByStock;
  const vehicles = useHideLenderVehicleField(vehiclesData, isMakeItWork ? dealSourceData : undefined);
  const totalVehicles = getTotalVehicles(filterView, isValidStock, vehicles);

  useEffect(() => {
    if (!isValidStock) dispatch(resetByStock());
  }, [dispatch, isValidStock]);

  useEffect(() => {
    if (!stocks.length && vehicles?.length) dispatch(resetByStock());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, vehicles?.length]);

  useEffect(() => {
    if (!user?.isWithInventory && feesAndTaxes.salesTaxRate === FEES_AND_TAXES_INITIAL_VALUES.salesTaxRate) {
      setValue(FEES_AND_TAXES_FIELD_NAMES.SALES_TAX_RATE, user?.lenderSettings?.tax_rate);
    }
  }, [setValue, user?.isWithInventory, user?.lenderSettings?.tax_rate, feesAndTaxes.salesTaxRate]);

  useFeesAndTaxesInitialValues(
    setValue,
    getLenderInitialValues,
    undefined,
    isMakeItWork,
    user?.isWithInventory,
    true,
    routeOneDealer,
  );
  useSettingsInitialValues(setValue, getLenderInitialValues, lenderState?.guidebooks?.[0]?.internal_value);

  const matchedTier = tiers.find((tier) => {
    if (tier.tier_name && dealSourceData?.finance_details?.tier) {
      const formattedSwaTier = tier.tier_name.replace(/\s/g, '').toLowerCase();
      const formattedDealSourceTier = dealSourceData.finance_details.tier.replace(/\s/g, '').toLowerCase();

      return formattedSwaTier === formattedDealSourceTier;
    }

    return false;
  });

  const areRouteOneFieldsEntered = routeOneSearch !== EMPTY_STRING && routeOneDealer !== undefined;

  useDealSourceValues(
    setValue,
    isDealSourceDataEmpty,
    dealSourceData,
    resetCount,
    tiers.length === 0,
    tier.max_allowed_pti,
    tier.max_allowed_payment,
    firstDealSourceRender,
    setDealSourceTierEditable,
    setFirstDealSourceRenderOnLender,
    !!matchedTier,
    matchedTier,
    tiers,
    isMakeItWork,
    user?.isWithInventory,
    false,
    isWithoutSSO || (!user?.is_sso && isRouteOneFlowForSbbOrSbc),
    areRouteOneFieldsEntered,
    true,
  );

  const { isArraFinance, defaultBackAdds } = useArraFinanceBackAdds();

  useEffect(() => {
    if (isArraFinance && defaultBackAdds > 0 && totalFrontAndBackEndsAddsDialog?.vsc === undefined) {
      setValue(FRONT_AND_BACK_ENDS_ADDS_FIELD_NAMES.VSC, defaultBackAdds);
    }
  }, [isArraFinance, defaultBackAdds, totalFrontAndBackEndsAddsDialog?.vsc, setValue]);

  useEffect(() => {
    if (isShopByCallbackMode && tiers.length > 0 && !tier.tier_id) {
      const firstTier = tiers[0];

      setValue(TIER_NAME, firstTier);
      setValue('preferences.ltvHighLimit', firstTier.ltv_limit);
    }
  }, [isShopByCallbackMode, tiers, tier.tier_id, setValue]);

  const { handleOpenDialog, isModalDialogActive } = useModalState([MODAL_DIALOG_ID.PREFERENCES]);

  const previewPlaceholder = (
    <Trans
      defaults={t('components.RACAffordabilityEngine.previewPlaceholderNoResults')}
      components={[<br />]}
      style={{ maxWidth: '700px' }}
    />
  );

  const onSubmit = useCallback(
    async (dealSourceData, currentFilterName) => {
      syncFormAction();

      const values = getValues() as ILenderForm;
      const areRequiredFieldsFilled = Boolean(
        values.grossMonthlyIncome && values.tier.tier_name && !Number.isNaN(values.feesAndTaxes.salesTaxRate ?? NaN),
      );

      if (areRequiredFieldsFilled) {
        setValue(currentFilterName, false);

        const isFilterByParams = values.filterView === FilterView.BY_PARAMS;

        const CURRENT_LENDER_FORM_NAMES_MAPPER = isFilterByParams
          ? LENDER_FORM_NAMES_MAPPER
          : LENDER_BY_STOCK_FORM_NAMES_MAPPER;

        if (typeof values.vehicleFilters.vehicleType === 'string') {
          values.condition = values.vehicleFilters.vehicleType;
        }

        // eslint-disable-next-line no-param-reassign
        if (!isFilterByParams) values.vehicleFilters.minMonthlyPayment = 0;

        let vehicleParameters = getSubmitVehicleObjectQuery<ILenderForm>(
          values,
          LenderService.getSubmitQuery,
          CURRENT_LENDER_FORM_NAMES_MAPPER,
        );

        if (
          dealSourceData?.finance_details?.apr &&
          dealSourceData?.transaction_type !== DealTransactionType.BALLOON_PAYMENT
        ) {
          vehicleParameters = { apr: dealSourceData.finance_details.apr, ...vehicleParameters };
        }

        const overrideApr = getOverrideApr({
          balloonToggle: false,
          isShopByCallbackMode,
          dealSourceOverrideApr: dealSourceData?.finance_details?.override_apr,
          dealSourceTransactionType: dealSourceData?.transaction_type,
        });

        if (overrideApr != null) {
          vehicleParameters = { override_apr: overrideApr, ...vehicleParameters };
        }

        vehicleParameters = {
          override_vsc: dealSourceData?.finance_details?.override_vsc,
          total_ltv_limit: dealSourceData?.finance_details?.total_ltv_limit,
          ...vehicleParameters,
          apply_cpo: Boolean(values.applyCpo),
          deal_source_ltv_high_limit: dealSourceData?.finance_details?.high_ltv_limit,
          ltv_high_limit: getLtvHighLimit({
            isShopByCallbackMode,
            sbcLtv: (values as any).shopByCallback?.sbcLtv,
            tierLtvLimit: values.tier?.ltv_limit,
            dealSourceLtv: dealSourceData?.finance_details?.high_ltv_limit,
          }),
        };

        const searchResult = isFilterByParams
          ? await dispatch(getVehiclesForLenderThunk(vehicleParameters))
          : await dispatch(getLenderVehiclesByStockThunk(vehicleParameters));

        if (searchResult.meta?.requestStatus === 'fulfilled') {
          trackSearch();
        }
      }
    },
    [syncFormAction, getValues, dispatch, isShopByCallbackMode, trackSearch, getOverrideApr, getLtvHighLimit, setValue],
  );

  const onSubmitRef = useRef(onSubmit);

  onSubmitRef.current = onSubmit;

  const dependentValues = {
    cashDownPayment,
    desiredMonthlyPayment,
    grossMonthlyIncome,
    tableView,
    tier: tier.tier_name,
    overrideApr: preferences.overrideApr,
    overrideLtv: preferences.overrideLtv,
    ltvHighLimit: preferences.ltvHighLimit,
    ltvTaxesFeesFilter: preferences.ltvTaxesFeesFilter,
    ...feesAndTaxes,
    frontAndBackEndsAddsAddOns: totalFrontAndBackEndsAddsDialog?.addOns,
    frontAndBackEndsAddsVsc: totalFrontAndBackEndsAddsDialog?.vsc,
    frontAndBackEndsAddsGap: totalFrontAndBackEndsAddsDialog?.gap,
    applyCpo,
    ...vehicleFilters,
    /* eslint-disable @typescript-eslint/indent */
    ...(isShopByCallbackMode && shopByCallback
      ? {
          sbcTerm: shopByCallback.sbcTerm?.value,
          sbcApr: shopByCallback.sbcApr,
          sbcLtv: shopByCallback.sbcLtv,
        }
      : {}),
    /* eslint-enable @typescript-eslint/indent */
  };

  const { debounced: onSubmitDebounced, cancel: cancelSubmitDebounced } = useDebounceCallback(
    (dealSourceData, currentFilterName) => {
      onSubmit(dealSourceData, currentFilterName);
    },
    REQUEST_DELAY,
  );

  const handleApplyCpoToggle = useCallback(() => {
    isApplyCpoToggledByUserRef.current = true;
  }, []);

  const handleSearchSubmit = useCallback(() => {
    if (isValid) onSubmit(dealSourceData, currentFilterName);
  }, [isValid, onSubmit, dealSourceData, currentFilterName]);

  const areSbcFieldsFilled =
    !isShopByCallbackMode ||
    Boolean(
      shopByCallback?.sbcTerm?.value &&
        shopByCallback?.sbcApr !== null &&
        shopByCallback?.sbcApr !== undefined &&
        shopByCallback?.sbcApr >= 0 &&
        shopByCallback?.sbcLtv !== null &&
        shopByCallback?.sbcLtv !== undefined &&
        shopByCallback?.sbcLtv > 0,
    );

  const areRequiredFieldsFilled = Boolean(
    grossMonthlyIncome && tier.tier_name && !Number.isNaN(feesAndTaxes.salesTaxRate ?? NaN) && areSbcFieldsFilled,
  );

  const tablePlaceholderType = useTablePlaceholderType(
    areRequiredFieldsFilled,
    vehicles || [],
    getVehicleFetchingFlags,
    isValidStock,
    isFetching,
  );

  useRequestBalloonInitData(areRequiredFieldsFilled);

  const debouncedIsValid = useDebounceVariable(isFormValid(), IS_VALID_DELAY);

  useEffect(() => {
    if (!areRequiredFieldsFilled) {
      vehicleApi.cancelGetLenderVehiclesRequests();
      vehicleApi.cancelGetLenderVehiclesByStockRequests();
    }
  }, [areRequiredFieldsFilled]);

  const handleChangeCallback = useCallback(() => {
    if (typeof totalVehicles !== 'undefined' && !debouncedIsValid) {
      dispatch(resetVehicles());
    }

    if (
      areRequiredFieldsFilled &&
      (isRequestNeeded || addVehicleFlags.isLoaded || updateVehicleFlags.isLoaded) &&
      isValidStock
    ) {
      const isApplyCpoToggle = isApplyCpoToggledByUserRef.current;

      isApplyCpoToggledByUserRef.current = false;
      setIsFetching(true);
      vehicleApi.cancelGetLenderVehiclesRequests();
      vehicleApi.cancelGetLenderVehiclesByStockRequests();

      if (isApplyCpoToggle) {
        cancelSubmitDebounced();
        onSubmitRef.current(dealSourceData, currentFilterName);
      } else {
        onSubmitDebounced(dealSourceData, currentFilterName);
      }

      if (addVehicleFlags.isLoaded) setShowCreatedVehicleErrorMessage(true);
    } else if (!areRequiredFieldsFilled || !isValidStock) {
      isApplyCpoToggledByUserRef.current = false;
      setIsFetching(false);
      cancelSubmitDebounced();
    }
  }, [
    totalVehicles,
    debouncedIsValid,
    areRequiredFieldsFilled,
    isRequestNeeded,
    addVehicleFlags.isLoaded,
    updateVehicleFlags.isLoaded,
    isValidStock,
    dispatch,
    onSubmitDebounced,
    dealSourceData,
    currentFilterName,
    cancelSubmitDebounced,
  ]);

  useEffect(() => {
    handleChangeCallback();
  }, [
    stocks.length,
    filterView,
    handleChangeCallback,
    // Inserting the dependentValues object initiates a comparison by reference and an additional hook launch.
    // This technique is used to avoid spreading the entire dependentValues object separately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ...Object.values(dependentValues),
  ]);

  useEffect(() => {
    if (areRequiredFieldsFilled) {
      dispatch(resetResetCount());
    }
  }, [dispatch, areRequiredFieldsFilled]);

  useEffect(() => {
    dispatch(getLenderThunk(lenderId));
    dispatch(getLenderControlsThunk(lenderId));
    setValue('lenderName', lenderName);
    setTimeout(() => {
      setShowLoader(true);
    }, REQUEST_DELAY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!areRequiredFieldsFilled) dispatch(setIsModalsDisabled({ value: true, key: DisabledModals.LENDER }));
    else dispatch(setIsModalsDisabled({ value: false, key: DisabledModals.LENDER }));
  }, [areRequiredFieldsFilled, dispatch]);

  useEffect(() => {
    if (!user?.is_sso && isLoadedLender) {
      dispatch(setIsModalsDisabled({ value: false, key: DisabledModals.REHASH_CALCULATOR }));
    }
  }, [dispatch, isLoadedLender, user?.is_sso]);

  useEffect(() => {
    if (!isLoadedLender) dispatch(setIsModalsDisabled({ value: true, key: DisabledModals.REHASH_CALCULATOR }));
  }, [dispatch, isLoadedLender]);

  useEffect(() => {
    if (user?.is_sso && isLoadedLender && user?.isWithInventory && dealSourceDataFlags.isReceived) {
      dispatch(setIsModalsDisabled({ value: false, key: DisabledModals.REHASH_CALCULATOR }));
    }
  }, [dispatch, isLoadedLender, user?.is_sso, user?.isWithInventory, dealSourceDataFlags.isReceived]);

  const isCreatedVehicleInList = useMemo(
    () => !!vehicles?.length && vehicles.some((vehicle) => vehicle.id === createdVehicle.id),
    [vehicles, createdVehicle.id],
  );

  const isDealSourceVehicleInList = useMemo(
    () => !!vehicles?.length && vehicles.some((vehicle) => vehicle.id === dealSourceData?.vehicle_id),
    [vehicles, dealSourceData?.vehicle_id],
  );

  const isDealSourceVehicleIncomplete = useMemo(
    () =>
      dealSourceData?.sale_vehicle &&
      dealSourceData?.manually_created &&
      (!dealSourceData.sale_vehicle?.retail_price || !dealSourceData?.sale_vehicle?.book_value),
    [dealSourceData?.sale_vehicle, dealSourceData?.manually_created],
  );

  useEffect(() => {
    if (
      !addVehicleFlags.isLoaded &&
      createdVehicle.id &&
      !currentVehicleData?.id &&
      createdUserVehicleTableName === TableName.LENDER &&
      createdUserVehicleLenderId === currentLenderId &&
      !isFetchingVehicles &&
      isCreatedVehicleInList
    ) {
      vehicleApi
        .getLenderLoanTermsList({
          mileage: createdVehicle.odometer,
          year: createdVehicle.year,
          tier: tier?.tier_name,
          lender_name: lenderName,
          balloon_payment: false,
          ...(r1DealerId && { r1_dealer_id: r1DealerId }),
          ...(conversationId && { conversation_id: conversationId }),
        })
        .then((response) => {
          if (!LenderService.hasLoanTerms(response)) {
            setShowCreatedVehicleErrorMessage(true);

            return;
          }

          dispatch(setAddedIsUserVehicle(true));
          handleLoanOpenDialog();
        });
    }

    if (addVehicleFlags.isLoaded && createdVehicle.id) {
      dispatch(clearLoanVariablesCurrentVehicle());
      handleLoanCloseDialog()();
    }
  }, [
    addVehicleFlags.isLoaded,
    createdVehicle.id,
    createdUserVehicleLenderId,
    currentLenderId,
    dispatch,
    currentVehicleData?.id,
    isFetchingVehicles,
    isCreatedVehicleInList,
  ]);

  useEffect(() => {
    if (addVehicleFlags.isFetching) setShowCreatedVehicleErrorMessage(false);
  }, [addVehicleFlags.isFetching]);

  useEffect(() => {
    setIsActive(!!lenderState.tiers.length);
  }, [lenderState]);

  useEffect(() => {
    if (createdVehicle.id) setAddedVehicleId(createdVehicle.id);
  }, [createdVehicle.id]);

  const redirectFreeLenders = useMemo(() => lenders.filter((l) => l.withoutLenderRedirect), [lenders]);
  const isDealerSsoLenderSelected = isDealerSsoLenderSelectionComplete(user, redirectFreeLenders, disabledFeaturesList);

  useEffect(() => {
    if (isDealSourceDataEmpty) dealSourceLoanTermsRequestKeyRef.current = null;
  }, [isDealSourceDataEmpty]);

  useEffect(() => {
    if (
      user?.is_sso &&
      isDealerSsoLenderSelected &&
      dealSourceDataFlags.isReceived &&
      !isDealSourceDataEmpty &&
      dealSourceData.vehicle_exists &&
      tier?.tier_id &&
      ((!user?.isWithInventory && !isModalsDisabled[DisabledModals.REHASH_CALCULATOR]) || user?.isWithInventory) &&
      !isShopByBudgetMode &&
      !isShopByCallbackMode &&
      hasValidDealSourceIncome &&
      (isDealSourceVehicleInList || isDealSourceVehicleIncomplete)
    ) {
      const dealSourceLoanTermsParams = {
        mileage: dealSourceData.sale_vehicle.mileage,
        year: parseInt(dealSourceData.sale_vehicle.year),
        tier: tier?.tier_name,
        lender_name: lenderName,
        balloon_payment: false,
        ...(r1DealerId && { r1_dealer_id: r1DealerId }),
        ...(conversationId && { conversation_id: conversationId }),
      };
      const dealSourceLoanTermsRequestKey = JSON.stringify(dealSourceLoanTermsParams);

      if (dealSourceLoanTermsRequestKeyRef.current !== dealSourceLoanTermsRequestKey) {
        dealSourceLoanTermsRequestKeyRef.current = dealSourceLoanTermsRequestKey;

        vehicleApi.getLenderLoanTermsList(dealSourceLoanTermsParams).then((response) => {
          if (!LenderService.hasLoanTerms(response)) {
            // Year/mileage outside Lender Controls — stay on Lender page with standard message
            setShowDealSourceLenderControlsMismatch(true);

            return;
          }

          setShowDealSourceLenderControlsMismatch(false);
          dispatch(setIsDealSourceVehicle(true));
          handleLoanOpenDialog();
        });

        sessionStorage.setItem(IS_SSO_AND_SHOW_LOAN_VARIABLES_DIALOG, 'true');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDealSourceDataEmpty,
    user?.is_sso,
    tier?.tier_id,
    user?.isWithInventory,
    isSsoAndLoanVariablesDialogOpen,
    hasValidDealSourceIncome,
    isDealSourceVehicleInList,
    isDealSourceVehicleIncomplete,
    lenders,
    disabledFeaturesList,
    dealerSsoLenderSelectionTick,
    isDealerSsoLenderSelected,
  ]);

  useEffect(() => {
    if (
      !user?.is_sso &&
      !isDealSourceDataEmpty &&
      dealSourceDataFlags.isReceived &&
      dealSourceData.vehicle_exists &&
      tier?.tier_id &&
      user?.isWithInventory &&
      dealSourceData?.lender_path === window.location.pathname &&
      !isShopByBudgetMode &&
      !isShopByCallbackMode &&
      hasValidDealSourceIncome &&
      (isDealSourceVehicleInList || isDealSourceVehicleIncomplete)
    ) {
      const dealSourceLoanTermsParams = {
        mileage: dealSourceData.sale_vehicle.mileage,
        year: parseInt(dealSourceData.sale_vehicle.year),
        tier: tier?.tier_name,
        lender_name: lenderName,
        balloon_payment: false,
        ...(r1DealerId && { r1_dealer_id: r1DealerId }),
        ...(conversationId && { conversation_id: conversationId }),
      };
      const dealSourceLoanTermsRequestKey = JSON.stringify(dealSourceLoanTermsParams);

      if (dealSourceLoanTermsRequestKeyRef.current !== dealSourceLoanTermsRequestKey) {
        dealSourceLoanTermsRequestKeyRef.current = dealSourceLoanTermsRequestKey;

        vehicleApi.getLenderLoanTermsList(dealSourceLoanTermsParams).then((response) => {
          if (!LenderService.hasLoanTerms(response)) {
            // Year/mileage outside Lender Controls — stay on Lender page with standard message
            setShowDealSourceLenderControlsMismatch(true);

            return;
          }

          setShowDealSourceLenderControlsMismatch(false);
          dispatch(setIsDealSourceVehicle(true));
          handleLoanOpenDialog();
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDealSourceDataEmpty,
    user?.is_sso,
    tier?.tier_id,
    user?.isWithInventory,
    hasValidDealSourceIncome,
    dealSourceDataFlags.isReceived,
    isDealSourceVehicleInList,
    isDealSourceVehicleIncomplete,
  ]);

  const title = <TitleContainer imgSrc={lenderState.logo} />;

  const firstConsumerFields = useMemo(() => {
    const fields = isShopByCallbackMode
      ? REHASH_CALCULATOR_FIELDS.filter((field) => field.name !== TIER_NAME)
      : REHASH_CALCULATOR_FIELDS;

    const mappedFields = fields.map((field) => {
      if (field.name === TIER_NAME) {
        return {
          ...field,
          controlProps: {
            ...field.controlProps,
            isRouteOneFlowForSbbOrSbc,
          },
        };
      }

      return field;
    });

    return mappedFields;
  }, [isShopByCallbackMode, isRouteOneFlowForSbbOrSbc]);

  useEffect(() => {
    if (isLenderLoading) return;

    dispatch(getRouteOneDealersThunk(!!lenderState?.lender_list));
  }, [dispatch, lenderState?.lender_list, isLenderLoading]);

  return isLenderLoading ? (
    showLoader ? (
      <Loader />
    ) : (
      <></>
    )
  ) : (
    <FormProvider {...methods}>
      {!isActive && <DisabledContainer />}
      <Form
        onSubmit={(event) => {
          event.preventDefault();
          handleSearchSubmit();
        }}
      >
        <Row>
          <AdaptedCol xs={ColumnWidth.FULL}>
            <ConsumerInformation
              submitCallback={handleChangeCallback}
              cancelSubmitDebounced={cancelSubmitDebounced}
              title={title}
              firstConsumerFields={firstConsumerFields}
              thirdConsumerFields={isShopByCallbackMode ? SHOP_BY_CALLBACK_FIELDS : []}
              isVehicleTableLoading={isFetchingVehicles}
              pageName={PageName.LENDER}
              totalVehicles={totalVehicles}
              areRequiredFieldsFilledIn={areRequiredFieldsFilled}
              fetchingVehicleFlagsSelector={getVehicleFetchingFlags}
              ltvTaxesFeesFilter={lenderState.ltv_calculation_method === LTV_INC_TAXES_FEES ? 'true' : 'false'}
              tableName={TableName.LENDER}
              isMakeItWork={isMakeItWork}
              settingsModalIsActive={isModalDialogActive}
              settingsModalHandleOpen={handleOpenDialog}
              settingsModalDisabled={isModalsDisabled[DisabledModals.REHASH_CALCULATOR]}
              isRouteOneFlowForSbbOrSbc={isRouteOneFlowForSbbOrSbc}
              isShopByCallbackMode={isShopByCallbackMode}
              isShopByBudgetMode={isShopByBudgetMode}
              isDealSourceDataEmpty={isDealSourceDataEmpty}
              lenderHasRouteOneConfig={lenderHasRouteOneConfig}
              isRouteOneFlow={isRouteOneFlow}
              advancedOverridesFields={showAdvancedOverrides ? ADVANCED_OVERRIDES_FIELDS : undefined}
            />
          </AdaptedCol>
        </Row>
        <Row>
          <AdaptedCol xs={ColumnWidth.FULL}>
            <VehicleSearchTable
              tableName={TableName.LENDER}
              tablePlaceholderType={tablePlaceholderType}
              isLender
              data={vehicles || []}
              tableSubmitCallback={handleSearchSubmit}
              defaultPlaceholderTitle={
                isShopByCallbackMode ? DEFAULT_PLACEHOLDER_TITLE_SHOP_BY_CALLBACK : DEFAULT_PLACEHOLDER_TITLE
              }
              previewPlaceholder={previewPlaceholder}
              isTableFiltersDisabled={!debouncedIsValid || !userHasInventory(user)}
              isTableActionsDisabled={!vehicles?.length || !areRequiredFieldsFilled}
              isTableView={isTableView}
              setIsTableView={setIsTableView}
              fetchingVehicleFlagsSelector={getVehicleFetchingFlags}
              reportDataSelector={getLenderReportData}
              CustomVehicleCard={LenderVehicleCard}
              filterControls={
                isAllyCpoLender ? (
                  <ApplyCpoCheckbox
                    name="applyCpo"
                    disabled={isFetchingVehicles}
                    changeCallback={handleApplyCpoToggle}
                  />
                ) : undefined
              }
              notAvailableVehicles={notAvailableVehicles}
              notFoundVehicles={notFoundVehicles}
              isFilterByStockNumber
              availableFilters={availableFilters}
              areRequiredFieldsFilledIn={areRequiredFieldsFilled}
              isMakeItWork={isMakeItWork}
              dealSourceData={dealSourceData}
              skipAutoStateReset={!resetCount}
              showNoVehicleErrorMessage={
                isMakeItWork &&
                filterView === FilterView.BY_PARAMS &&
                !isDealSourceDataEmpty &&
                !!vehicles?.length &&
                !dealSourceData.vehicle_exists
              }
              showNoVehicleInInventoryErrorMessage={
                showDealSourceLenderControlsMismatch ||
                (isMakeItWork &&
                  filterView === FilterView.BY_PARAMS &&
                  !isDealSourceDataEmpty &&
                  !!vehicles?.length &&
                  dealSourceData.vehicle_exists &&
                  !vehicles.map((vehicle) => vehicle.id).includes(dealSourceData.vehicle_id))
              }
              tier={tier.tier_name}
              showNoAddedVehicleErrorMessage={
                !!vehicles?.length &&
                createdUserVehicleTableName === TableName.LENDER &&
                createdUserVehicleLenderId === currentLenderId &&
                showCreatedVehicleErrorMessage &&
                !isFetching &&
                !vehicles.map((vehicle) => vehicle.id).includes(addedVehicleId)
              }
              showNoResultsAndDealSourceVehicle={
                !showDealSourceLenderControlsMismatch &&
                isMakeItWork &&
                filterView === FilterView.BY_PARAMS &&
                !isDealSourceDataEmpty &&
                !!vehicles &&
                !vehicles?.length &&
                dealSourceData.vehicle_exists
              }
            />
          </AdaptedCol>
        </Row>
      </Form>
      {isModalLoanDialogActive && (
        <LenderLoanVariablesDialog
          isLoanVariablesDialogActive={isModalLoanDialogActive}
          resetState={() => {
            dispatch(resetAddedIsUserVehicle());
            dispatch(resetIsDealSourceVehicle());
            dispatch(resetCreatedUserVehicle());
          }}
          onHide={() => {
            handleLoanCloseDialog()();
            vehicleApi.cancelBalloonVehicleInfoRequests();
          }}
          isDealSourceCar={isDealSourceVehicle}
          isMakeItWork={isMakeItWork}
          isSubmitR1DealBlockedByControls={isSubmitR1DealBlockedByControls}
          isSalespersonLimitedView={isSalesperson}
          isRouteOneFlowForSbbOrSbc={isRouteOneFlowForSbbOrSbc}
          isDealerSsoSbbOrSbcFlow={isDealerSsoSbbOrSbcFlow}
          isShopByCallbackMode={isShopByCallbackMode}
          isRouteOneFlow={isRouteOneFlow}
        />
      )}
      {user?.is_sso && !user?.isWithInventory && isDealerSsoLenderSelected && (
        <SetupTaxRateModal
          userLenderSettings={user.lenderSettings}
          isReady={isLoadedLender}
          onSave={() => {
            dispatch(setIsModalsDisabled({ value: false, key: DisabledModals.UNLOCK_INVENTORY }));
            dispatch(setIsModalsDisabled({ value: false, key: DisabledModals.REHASH_CALCULATOR }));

            if (tier?.tier_name && !isShopByBudgetMode && !isShopByCallbackMode && hasValidDealSourceIncome) {
              vehicleApi
                .getLenderLoanTermsList({
                  mileage: dealSourceData.sale_vehicle.mileage,
                  year: parseInt(dealSourceData.sale_vehicle.year),
                  tier: tier?.tier_name,
                  lender_name: lenderName,
                  balloon_payment: false,
                  ...(r1DealerId && { r1_dealer_id: r1DealerId }),
                  ...(conversationId && { conversation_id: conversationId }),
                })
                .then((response) => {
                  if (!LenderService.hasLoanTerms(response)) {
                    // Year/mileage outside Lender Controls — stay on Lender page with standard message
                    setShowDealSourceLenderControlsMismatch(true);

                    return;
                  }

                  setShowDealSourceLenderControlsMismatch(false);
                  dispatch(setIsDealSourceVehicle(true));
                  handleLoanOpenDialog();
                });

              sessionStorage.setItem(IS_SSO_AND_SHOW_LOAN_VARIABLES_DIALOG, 'true');
            }
          }}
        />
      )}
    </FormProvider>
  );
};
